import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';

import enHelp from '@/help/help.en.md?raw';
import ukHelp from '@/help/help.uk.md?raw';

interface TocEntry {
  level: 2 | 3;
  slug: string;
  title: string;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9а-яёіїєґ\s-]/giu, '')
    .trim()
    .replace(/\s+/g, '-');
}

function extractToc(md: string): TocEntry[] {
  const out: TocEntry[] = [];
  for (const line of md.split('\n')) {
    const m2 = /^##\s+(.+)$/.exec(line);
    const m3 = /^###\s+(.+)$/.exec(line);
    if (m2) out.push({ level: 2, slug: slugify(m2[1]!), title: m2[1]! });
    else if (m3) out.push({ level: 3, slug: slugify(m3[1]!), title: m3[1]! });
  }
  return out;
}

export default function HelpPage() {
  const { i18n, t } = useTranslation('help');
  const lang = i18n.language.startsWith('uk') ? 'uk' : 'en';
  const md = lang === 'uk' ? ukHelp : enHelp;
  const toc = useMemo(() => extractToc(md), [md]);

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">
      <article className="prose prose-sm max-w-3xl flex-1 dark:prose-invert">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children }) => <h1 className="text-2xl font-semibold">{children}</h1>,
            h2: ({ children }) => {
              const slug = typeof children === 'string' ? slugify(children) : undefined;
              return (
                <h2 id={slug} className="mt-6 text-xl font-semibold">
                  {children}
                </h2>
              );
            },
            h3: ({ children }) => {
              const slug = typeof children === 'string' ? slugify(children) : undefined;
              return (
                <h3 id={slug} className="mt-4 text-base font-semibold">
                  {children}
                </h3>
              );
            },
            table: ({ children }) => (
              <table className="my-2 w-full border-collapse text-xs">{children}</table>
            ),
            th: ({ children }) => (
              <th className="border bg-muted/50 px-2 py-1 text-left font-medium">{children}</th>
            ),
            td: ({ children }) => <td className="border px-2 py-1 align-top">{children}</td>,
            code: ({ children }) => (
              <code className="rounded bg-muted px-1 py-0.5 text-[11px]">{children}</code>
            ),
          }}
        >
          {md}
        </ReactMarkdown>
      </article>
      <aside className="w-full shrink-0 lg:sticky lg:top-6 lg:w-56 lg:self-start">
        <div className="rounded-md border bg-card p-3 text-xs">
          <div className="mb-2 text-muted-foreground">{t('toc')}</div>
          <nav>
            <ul className="space-y-1">
              {toc.map((e) => (
                <li
                  key={`${e.level}-${e.slug}`}
                  className={e.level === 3 ? 'ml-3 text-muted-foreground' : ''}
                >
                  <a href={`#${e.slug}`} className="hover:underline">
                    {e.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </aside>
    </div>
  );
}
